import {useTeamPageProjectsQuery} from '../../generated/graphql';

/**
 * Given (T | undefined)[], running .filter(n => !!n) would exclude all the falsy values from the array. However, TS isn't smart enough to know that the return is T[] so we have to give it a little extra help with a typed filter fn
 */
export function exists<T>(value: T | null | undefined): value is T {
  return !!value;
}

type Run = {
  id: string;
  displayName: string;
  description: string;
  user: {
    id: string;
    username: string;
    photoUrl: string;
    admin: boolean;
  };
  project: {
    id: string;
    entityName: string;
    name: string;
    access: string;
  };
  starCount: number;
  updatedAt: string;
  starred: boolean;
  showcasedAt: string;
  previewUrl: string;
};
type Project = {
  id: string;
  access: string;
  name: string;
  entityName: string;
};
type TeamPageProjectsData = {
  data: {
    entity: {
      id: string;
      organizationId: string;
      projects: Project[];
      runs: Run[];
    };
  } | null;
  loading: boolean;
};

export const useTeamPageProjects = (
  entityName: string
): TeamPageProjectsData => {
  const {data, loading} = useTeamPageProjectsQuery({
    variables: {entityName},
  });

  if (!data) {
    return {
      data: null,
      loading,
    };
  }

  const projectNodes =
    data.entity?.projects?.edges.map(e => e.node).filter(exists) || [];

  const projects =
    projectNodes.map(projNode => ({
      id: projNode.id || '',
      access: projNode.access || '',
      name: projNode.name || '',
      entityName: projNode.entityName || '',
    })) || [];

  const runs = projectNodes
    .map(projNode => {
      const viewNodes =
        projNode.allViews?.edges.map(edge => edge.node).filter(exists) || [];

      return viewNodes.map(view => ({
        ...view,
        description: view.description || '',
        displayName: view.displayName || '',
        previewUrl: view.previewUrl || '',
        project: {
          id: view.project?.id || '',
          entityName: view.project?.entityName || '',
          name: view.project?.name || '',
          access: view.project?.access || '',
        },
        showcasedAt: view.showcasedAt?.toString() || '',
        starred: view.starred || false,
        updatedAt: view.updatedAt?.toString() || '',
        user: {
          id: view.user?.id || '',
          username: view.user?.username || '',
          photoUrl: view.user?.photoUrl || '',
          admin: view.user?.admin || false,
        },
      }));
    })
    .flat();

  return {
    data: {
      entity: {
        id: data.entity?.id || '',
        organizationId: data.entity?.organizationId || '',
        projects,
        runs,
      },
    },
    loading,
  };
};
